import React, { createContext, useContext, useLayoutEffect, useRef, useState, useEffect } from 'react';
import { ScreenLayout } from './ScreenLayout';
import { useAppStore } from '../store/appStore';

export interface PaneShellConfig {
  header?: React.ReactNode;
  formHeader?: React.ReactNode;
  showBottomTabs?: boolean;
  onTabPress?: (tabId: string) => void;
  rightPaneContent?: 'contacts' | 'edit-contact' | 'details' | 'custom' | 'empty' | 'settings';
  rightPaneCustomContent?: React.ReactNode;
  /** What the right pane shows (chat, call history, a form…): the icon and label of its tab beside the AI assistant. */
  rightPaneTab?: 'chat' | 'calls' | 'form';
  selectedContact?: any;
  onContactSave?: (contact: any) => void;
  onContactDelete?: (contactId: string) => void;
  onRightPaneClose?: () => void;
  onContactPress?: (contact: any) => void;
  contactsRefreshTrigger?: number;
  /** Id of the contact currently open elsewhere (the screen's own middle
   * content, or the embedded conversation in the right pane) — shown as a
   * visual "active" mark on that row in the shared left-pane ContactsList. */
  activeContactId?: string;
  /** Overrides the shared/persisted right-pane width for just this content —
   * e.g. the embedded conversation pane wants more room than the default,
   * same mechanism PostsListScreen.tsx already uses for its own wider-pane
   * need. Forwarded straight to ScreenLayout's own existing prop of the
   * same name. */
  rightPaneWidthOverride?: number;
}

const DEFAULT_CONFIG: PaneShellConfig = {};

const PaneShellConfigContext = createContext<(config: PaneShellConfig) => void>(() => {});

function shallowEqualConfig(a: PaneShellConfig, b: PaneShellConfig): boolean {
  const aKeys = Object.keys(a) as (keyof PaneShellConfig)[];
  const bKeys = Object.keys(b) as (keyof PaneShellConfig)[];
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every(key => a[key] === b[key]);
}

/** Registers this screen's desired shell chrome (header, formHeader, right
 * pane, etc.) with the nearest PersistentPaneShell ancestor — called
 * unconditionally (rules-of-hooks) by every screen that can live inside the
 * shell. Outside a PersistentPaneShell (native/narrow-mobile-web, where
 * screens render their own <ScreenLayout> instead) the context default is a
 * no-op, so this is always safe to call.
 *
 * `isActive` must be false whenever this screen is mounted but not the
 * currently-displayed one — AppNavigator keeps all visited screens in this
 * group mounted (hidden via display:none) rather than unmounting them on
 * every switch, specifically so each screen's OWN content/data survives
 * navigating away and back (the whole point of the persistent shell).
 * Without this flag, a hidden screen re-rendering for its own unrelated
 * reasons (e.g. a timer, a socket event) would still call setConfig and
 * silently overwrite the ACTIVE screen's header/right-pane with its own —
 * e.g. Home's header flashing in while Chat is the one actually visible.
 *
 * Uses useLayoutEffect, not useEffect: a plain useEffect fires AFTER paint,
 * so on a Home→Contacts navigation the shell would first repaint with the
 * PREVIOUS screen's stale config (old header, old right-pane content) for
 * one visible frame before the new screen's effect corrects it.
 * useLayoutEffect runs synchronously before the browser paints, so the
 * correction lands in the same frame.
 *
 * No dependency array — re-registers on every render of the calling screen.
 * This is safe, not an infinite loop: PersistentPaneShell's own setState
 * only re-renders itself and reconciles `children`, and since `children` is
 * the same element reference passed down by AppNavigator (not recreated by
 * the shell's internal state change), React bails out of re-rendering the
 * calling screen just because the shell updated — the effect only re-fires
 * when the screen itself genuinely re-renders. The shallow-equality guard
 * below additionally skips the update entirely when nothing actually
 * changed, since AppContext's non-memoized value (AppNavigator.tsx)
 * re-renders every screen more often than the screen's own logic alone
 * would (e.g. every second during an active call) — without the guard,
 * each of those would still push a new config object (fresh inline header
 * element) and force an extra render pass through ScreenLayout/LeftPane/
 * ContactsList for no visible change. */
export function usePaneShellConfig(config: PaneShellConfig, isActive: boolean = true) {
  const setConfig = useContext(PaneShellConfigContext);
  const lastConfigRef = useRef<PaneShellConfig | null>(null);

  useLayoutEffect(() => {
    if (!isActive) return;
    if (lastConfigRef.current && shallowEqualConfig(lastConfigRef.current, config)) {
      return;
    }
    lastConfigRef.current = config;
    setConfig(config);
  });
}

interface PersistentPaneShellProps {
  children: React.ReactNode;
}

/** Mounted once by AppNavigator for the group of screens that share a
 * persistent contacts pane (Home/Contacts/Chat, desktop web only) — see
 * /Users/aydahab.mac/.claude/plans/iterative-squishing-lark.md for the full
 * rationale. Owns the single <ScreenLayout> instance (and therefore the
 * single LeftPane/ContactsList instance) for the whole group; `children`
 * swaps per navigation, but this shell itself never unmounts while the user
 * stays within the group. */
export const PersistentPaneShell: React.FC<PersistentPaneShellProps> = ({ children }) => {
  const [config, setConfig] = useState<PaneShellConfig>(DEFAULT_CONFIG);
  const setLeftPaneCollapsed = useAppStore(state => state.setLeftPaneCollapsed);

  // Force the contacts pane open on entering this group of screens — same
  // behavior ContactsListScreen.tsx used to own for itself alone; now it
  // only needs to fire once per entry into the group (this component's own
  // mount), not once per screen inside it, since the pane itself no longer
  // remounts when moving between them.
  useEffect(() => {
    setLeftPaneCollapsed(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PaneShellConfigContext.Provider value={setConfig}>
      <ScreenLayout
        header={config.header}
        formHeader={config.formHeader}
        showBottomTabs={config.showBottomTabs}
        onTabPress={config.onTabPress}
        onLeftPaneClose={() => {}}
        leftPaneContent="contacts"
        onContactPress={config.onContactPress}
        contactsRefreshTrigger={config.contactsRefreshTrigger}
        activeContactId={config.activeContactId}
        onRightPaneClose={config.onRightPaneClose}
        rightPaneContent={config.rightPaneContent}
        rightPaneCustomContent={config.rightPaneCustomContent}
        rightPaneTab={config.rightPaneTab}
        rightPaneWidthOverride={config.rightPaneWidthOverride}
        selectedContact={config.selectedContact}
        onContactSave={config.onContactSave}
        onContactDelete={config.onContactDelete}
      >
        {children}
      </ScreenLayout>
    </PaneShellConfigContext.Provider>
  );
};
